"use client";

import * as React from "react";
import Section from "@/components/section";
import { motion } from "src/lib/motion";
import { cn } from "@/lib/utils";
import BlurFade from "@/components/magicui/blur-fade";
import Image from "next/image";
import { Globe } from "lucide-react";
import type { Locale } from "@/lib/team-data";

import type { StaticImageData } from "next/image";

interface TeamMemberProps {
  readonly name: string;
  readonly role: string;
  readonly image: StaticImageData | string;
  readonly bio: string;
  readonly expertise: readonly string[];
  readonly className?: string;
  readonly objectPosition?: string;
}

function TeamMember({ name, role, image, bio, expertise, className, objectPosition = "center" }: Readonly<TeamMemberProps>) {
  const [isInfoOpen, setIsInfoOpen] = React.useState(false);

  React.useEffect(() => {
    console.log(isInfoOpen);
    
  }, [isInfoOpen])
  return (
    <div className={cn("text-center group", className)}>
      <div className="relative overflow-hidden rounded-lg mb-4 aspect-square shadow-lg">
      <button onClick={() => setIsInfoOpen(true)} className={cn("max-lg z-10 lg:hidden transition-opacity duration-300 w-6 h-6 absolute bottom-2 right-2", {
        "max-lg:opacity-0 max-lg:pointer-events-none": isInfoOpen === true,
      })}>
        <svg width="24px" height="24px" id="ac41e920-1668-4547-aea3-9619e77dda05" viewBox="0 0 38 38" xmlns="http://www.w3.org/2000/svg"><path fill="#1fc178" d="M19,.56A18.44,18.44,0,1,0,37.44,19,18.461,18.461,0,0,0,19,.56Z"/><circle cx="19.017" cy="26.242" fill="#fff" r="1.878"/><path d="M19.018,21.95h-.013a1.251,1.251,0,0,1-1.238-1.262c.016-1.638,1.362-3.326,2.551-4.815.245-.307.479-.6.683-.872a1.517,1.517,0,0,0,.067-1.61,2.368,2.368,0,0,0-2-1.34h0c-1.447,0-2.629.87-2.629,1.9a1.25,1.25,0,0,1-2.5,0c0-2.427,2.281-4.4,5.085-4.4l.12,0a4.831,4.831,0,0,1,4.173,2.734A3.975,3.975,0,0,1,23,16.5c-.218.291-.468.605-.73.934-.795,1-2,2.5-2,3.279A1.25,1.25,0,0,1,19.018,21.95Z" fill="#fff"/></svg>
      </button>
        <Image
          src={typeof image === 'string' ? image : image.src}
          alt={`${name} - ${role}`}
          className="object-cover w-full h-full transition-transform duration-500 lg:group-hover:scale-110"
          style={{ objectPosition }}
          width={500}
          height={500}
        />
        <div className={cn("absolute inset-0 bg-primary/80 opacity-0 lg:group-hover:opacity-100 transition-opacity duration-300 flex flex-col justify-center p-6 text-white", {
          "max-lg:opacity-100": isInfoOpen === true,
        })}>
          <p className="text-sm mb-4">{bio}</p>
          <div className="flex flex-wrap justify-center gap-2">
            {expertise.map((skill, index) => (
              <span 
                key={index} 
                className="px-2 py-1 bg-white/20 rounded-full text-xs"
              >
                {skill}
              </span>
            ))}
          </div>
          <button className={cn("w-6 h-6 rounded-full absolute bottom-2 right-2 transition-opacity duration-300 lg:hidden", {
            "max-lg:opacity-0 max-lg:pointer-events-none": isInfoOpen === false
          })} onClick={() => setIsInfoOpen(!isInfoOpen)}>
            <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M9.70711 8.29289C9.31658 7.90237 8.68342 7.90237 8.29289 8.29289C7.90237 8.68342 7.90237 9.31658 8.29289 9.70711L10.5858 12L8.29289 14.2929C7.90237 14.6834 7.90237 15.3166 8.29289 15.7071C8.68342 16.0976 9.31658 16.0976 9.70711 15.7071L12 13.4142L14.2929 15.7071C14.6834 16.0976 15.3166 16.0976 15.7071 15.7071C16.0976 15.3166 16.0976 14.6834 15.7071 14.2929L13.4142 12L15.7071 9.70711C16.0976 9.31658 16.0976 8.68342 15.7071 8.29289C15.3166 7.90237 14.6834 7.90237 14.2929 8.29289L12 10.5858L9.70711 8.29289Z" fill="#FFF"/>
              <path fillRule="evenodd" clipRule="evenodd" d="M12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2ZM4 12C4 7.58172 7.58172 4 12 4C16.4183 4 20 7.58172 20 12C20 16.4183 16.4183 20 12 20C7.58172 20 4 16.4183 4 12Z" fill="#FFF"/>
            </svg>
          </button>
        </div>
      </div>
      <h3 className="font-semibold text-lg">{name}</h3>
      <p className="text-sm text-primary text-muted-foreground">{role}</p>
    </div>
  );
}

interface TeamClientProps {
  readonly data: {
    readonly title: string;
    readonly subtitle: string;
    readonly members: readonly TeamMemberProps[];
    readonly networkText: string;
  };
}

export default function TeamClient({ data }: TeamClientProps) {
  return (
    <Section containerClassName="!px-0" className="!pb-8">
      <div className="container mx-auto px-4">
        <div className="text-center mb-12">
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            viewport={{ once: true }}
            className="text-3xl font-bold mb-4 break-words"
          >
            {data.title}
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
            viewport={{ once: true }}
            className="text-muted-foreground max-w-2xl mx-auto"
          >
            {data.subtitle}
          </motion.p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-10 max-w-5xl mx-auto">
          {data.members.map((member, index) => (
            <BlurFade key={index} delay={0.1 * index} inView>
              <TeamMember {...member} />
            </BlurFade>
          ))}
        </div>

        {/* Global Network Section */}
        <BlurFade delay={0.1 * data.members.length + 0.1} inView>
          <div className="mt-16 text-center border-t border-border/50 pt-8 max-w-3xl mx-auto">
            <div className="inline-flex items-center justify-center p-3 bg-primary/10 rounded-full mb-4">
              <Globe className="h-6 w-6 text-primary" />
            </div>
            <p className="text-lg text-muted-foreground max-md:text-center">
              {data.networkText.split(/20\+ spezialisierten KI-Experten|20\+ specialized AI experts/).map((part, i) => 
                i === 0 ? part : (
                  <React.Fragment key={i}>
                    <span className="font-semibold text-foreground">
                      {data.networkText.includes('spezialisierten') ? '20+ spezialisierten KI-Experten' : '20+ specialized AI experts'}
                    </span>
                    {part}
                  </React.Fragment>
                )
              )}
            </p>
          </div>
        </BlurFade>
      </div>
    </Section>
  );
}
